import { useState, Fragment } from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from '@/components/ui/table'
import { Sprout, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export default function SpawnSelectionTable({
  visibleBatches,
  selectedBagIds,
  onBagToggle,
  loading
}) {
  const [expandedBatchId, setExpandedBatchId] = useState(null)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-content-secondary text-sm">
        Loading spawn batches...
      </div>
    )
  }

  if (visibleBatches.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-content-secondary">
        <Sprout className="h-8 w-8 text-content-tertiary" />
        <span className="text-sm">No colonised spawn available</span>
      </div>
    )
  }

  const handleRowClick = (batch) => {
    const bags = batch.available_bags || []
    if (bags.length === 0) return

    if (bags.length === 1) {
      // Single bag: toggle directly
      onBagToggle(bags[0].id)
    } else {
      // Multiple bags: toggle expansion
      setExpandedBatchId(prev => prev === batch.id ? null : batch.id)
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Code</TableHead>
          <TableHead>Strain</TableHead>
          <TableHead className="text-right">Bag Weight</TableHead>
          <TableHead className="text-right">Bags</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {visibleBatches.map(batch => {
          const bags = batch.available_bags || []
          const totalBags = bags.length
          const selectedCount = bags.filter(b => selectedBagIds.has(b.id)).length
          const isSelected = selectedCount > 0
          const isExpanded = expandedBatchId === batch.id
          const hasMultipleBags = bags.length > 1

          return (
            <Fragment key={batch.id}>
              {/* Main batch row */}
              <TableRow
                onClick={() => handleRowClick(batch)}
                className={cn(
                  'cursor-pointer transition-colors',
                  isSelected
                    ? 'bg-black text-white hover:bg-neutral-800'
                    : 'hover:bg-surface-secondary/50'
                )}
              >
                <TableCell>
                  <div className="flex items-center">
                    <span className="w-5 flex-shrink-0">
                      {hasMultipleBags && (
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform',
                            isExpanded && 'rotate-180',
                            isSelected ? 'text-white' : 'text-content-tertiary'
                          )}
                        />
                      )}
                    </span>
                    <span className={cn('font-mono text-sm font-medium', isSelected && 'text-white')}>
                      {batch.code}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className={cn('text-sm', isSelected && 'text-white')}>
                    {batch.strain_name}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span className={cn('text-sm', isSelected && 'text-white')}>
                    {batch.bag_weight_kg}kg
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span className={cn('text-sm', isSelected && 'text-white')}>
                    {isSelected
                      ? `${selectedCount}/${totalBags} selected`
                      : `${totalBags} available`
                    }
                  </span>
                </TableCell>
              </TableRow>

              {/* Expanded bag rows */}
              {isExpanded && bags.map(bag => {
                const isBagSelected = selectedBagIds.has(bag.id)
                return (
                  <TableRow
                    key={bag.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      onBagToggle(bag.id)
                    }}
                    className={cn(
                      'cursor-pointer transition-colors',
                      isBagSelected
                        ? 'bg-black text-white hover:bg-neutral-800'
                        : 'bg-surface-secondary/30 hover:bg-surface-secondary/50'
                    )}
                  >
                    <TableCell>
                      <span className={cn('font-mono text-sm pl-8', isBagSelected && 'text-white')}>
                        {batch.code}-{bag.bag_number}
                      </span>
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right">
                      <span className={cn('text-sm', isBagSelected && 'text-white')}>
                        {batch.bag_weight_kg}kg
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={cn('text-sm', isBagSelected && 'text-white')}>
                        {isBagSelected ? 'Selected' : 'Available'}
                      </span>
                    </TableCell>
                  </TableRow>
                )
              })}
            </Fragment>
          )
        })}
      </TableBody>
    </Table>
  )
}
